// Auto-extracted from app.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { analyzeNineQuestions } from '../../utils/eqScoring';
import { TextWithSourceBadges } from '../../utils/sourceBadges';
import { EQScoreRing } from '../ui/EQScoreRing';

export function NineQuestionsPanel({ item, onEdit }) {
  const analysis = analyzeNineQuestions(item);
  
  // Render a single EQ card
  const EQCard = ({ q }) => {
    const statusIcon = q.isComplete 
      ? <span className="text-emerald-500">✓</span>
      : q.hasPlaceholder 
        ? <span className="text-red-500">⚠</span>
        : <span className="text-amber-500">○</span>;
    
    return (
      <div className={`rounded-lg border p-4 transition-all ${
        q.isComplete 
          ? "bg-emerald-50/50 border-emerald-200" 
          : q.hasPlaceholder 
            ? "bg-red-50/50 border-red-200"
            : "bg-amber-50/50 border-amber-200"
      }`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{q.num}.</span>
            <span className="font-semibold text-slate-900">{q.label}</span>
            {statusIcon}
          </div>
          {onEdit && (
            <button 
              onClick={() => onEdit(q.label)} 
              className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
            >
              Edit
            </button>
          )}
        </div>
        <p className="text-sm text-slate-600 mb-2">{q.desc}</p>
        {q.value ? (
          <div className="text-sm text-slate-900 bg-white/70 rounded px-2 py-1 border border-slate-100">
            {/* Show source badges for WHY field */}
            {q.label === "WHY" ? (
              <TextWithSourceBadges text={q.value} maxLength={200} />
            ) : (
              q.value.length > 100 ? q.value.slice(0, 100) + "..." : q.value
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-500 italic">Not provided</div>
        )}
        {q.hasPlaceholder && (
          <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
            <span>⚠</span> Placeholder detected: "{q.placeholders.join('", "')}"
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-4">
      {/* Header with score */}
      <div className={`rounded-lg p-4 ${analysis.status.bgColor} ${analysis.status.borderColor} border`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <EQScoreRing score={analysis.score} size={56} />
            <div>
              <div className="font-semibold text-slate-900">
                Completion: {analysis.score}/9 — <span className={`text-${analysis.status.color}-600`}>{analysis.status.label}</span>
              </div>
              {analysis.missing.length > 0 && (
                <div className="text-sm text-slate-600">
                  Missing: {analysis.missing.join(", ")}
                </div>
              )}
              {analysis.hasAnyPlaceholder && (
                <div className="text-sm text-red-600 font-medium">
                  ⚠ Placeholder rationale detected — blocks attestation
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* 4x2 Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Left column: 1-4 */}
        <div className="space-y-4">
          {analysis.results.slice(0, 4).map(q => <EQCard key={q.num} q={q} />)}
        </div>
        {/* Right column: 5-9 */}
        <div className="space-y-4">
          {analysis.results.slice(4, 9).map(q => <EQCard key={q.num} q={q} />)}
        </div>
      </div>
    </div>
  );
}
